import { CoinsVal } from "@components/icons/coins";
import { ResponsiveSelect } from "@components/responsive-select";
import UpgradeNeeded from "@components/upgrade-needed";
import { useImage, useImages } from "@features/images/use-images";
import { usePlanBetterOrEq } from "@features/plans";
import { useNavigateAlt } from "@features/utils";
import { ArrowsPointingOutIcon } from "@heroicons/react/16/solid";
import { Button, Heading, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface StudioUpscaleProps {
  image: any;
}

export const StudioUpscale = ({ image }: StudioUpscaleProps) => {
  const [loading, setLoading] = useState(false);
  const { upscale } = useImage(image.id);
  const navigate = useNavigateAlt();
  const { t } = useTranslation();
  const { images: relatedImages } = useImages("from:" + image.id);

  const isPlanBetterOrEq = usePlanBetterOrEq();
  const has4MP = isPlanBetterOrEq("essential");
  const has9MP = isPlanBetterOrEq("advanced");
  const has16MP = isPlanBetterOrEq("business");

  const [resolution, setResolution] = useState<"2" | "3" | "4">(
    has4MP ? "2" : has9MP ? "3" : has16MP ? "4" : "2"
  );

  return (
    <div className="space-y-4">
      <Heading size="3">{t("studio.upscale.title")}</Heading>
      <Text size="2" color="gray">
        {t("studio.upscale.description")}
      </Text>

      {relatedImages.data?.some((a: any) => a.sub_type === "upscale") ? (
        <Button
          size={"2"}
          className="w-full"
          loading={loading}
          disabled={relatedImages.isLoading}
          onClick={async () => {
            navigate(
              "/studio/" +
                relatedImages.data?.find((a: any) => a.sub_type === "upscale")
                  ?.id || ""
            );
          }}
        >
          <ArrowsPointingOutIcon className="w-4 h-4 mr-2" />
          {t("studio.upscale.go_to_upscaled")}
        </Button>
      ) : (
        <>
          <ResponsiveSelect
            value={resolution}
            onChange={(value) => setResolution(value as any)}
            items={[
              {
                value: "2",
                label:
                  t("studio.upscale.resolution_2x") +
                  (!has4MP ? " " + t("studio.upscale.upgrade_needed") : ""),
                disabled: !has4MP,
              },
              {
                value: "3",
                label:
                  t("studio.upscale.resolution_3x") +
                  (!has9MP ? " " + t("studio.upscale.upgrade_needed") : ""),
                disabled: !has9MP,
              },
              {
                value: "4",
                label:
                  t("studio.upscale.resolution_4x") +
                  (!has16MP ? " " + t("studio.upscale.upgrade_needed") : ""),
                disabled: !has16MP,
              },
            ]}
            className="w-full"
            renderTrigger={(value) => <>{value?.label}</>}
          />

          <div>
            <UpgradeNeeded
              minimumPlan={
                resolution === "4"
                  ? "business"
                  : resolution === "3"
                  ? "advanced"
                  : "essential"
              }
              minimumCredits={16}
            >
              <Button
                size={"2"}
                className="w-full"
                loading={loading}
                disabled={relatedImages.isLoading}
                onClick={async () => {
                  setLoading(true);
                  try {
                    await upscale(parseInt(resolution));
                  } catch (e) {
                    console.error("Upscale failed", e);
                  }
                  setLoading(false);
                }}
              >
                <ArrowsPointingOutIcon className="w-4 h-4 mr-2" />
                <span>
                  {t("components.camera-roll-image.upscale")}{" "}
                  <CoinsVal val={32} />
                </span>
              </Button>
            </UpgradeNeeded>
          </div>
        </>
      )}
    </div>
  );
};
